/**
 * @fileoverview Admin User Management Page
 * Search players, view account details, suspend or reinstate accounts.
 */

import React, { useState, useCallback } from 'react';
import {
  collection, query, where, getDocs, limit,
  collection as col,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../services/firebase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import './AdminUsersPage.css';

const DEACTIVATION_REASONS = [
  'Repeated terms of service violations',
  'Confirmed underage player',
  'Identity fraud / multiple accounts',
  'Harassment or threatening behaviour',
  'Problem gambling — self exclusion',
  'Problem gambling — third party request',
  'Responsible gambling concern',
  'Excessive play / compulsive behaviour',
  'Player requested account closure',
  'Other',
];

const PERMANENT_BAN_REASONS = [
  'Severe harassment or threatening behaviour',
  'Criminal activity or fraud',
  'Confirmed underage — repeat attempt',
  'Severe cheating or collusion',
  'Problem gambling — permanent self exclusion',
  'Problem gambling — third party permanent request',
  'Court order or regulatory requirement',
  'Other',
];

const SUSPENSION_REASONS = [
  'Multiple account creation',
  'Leaderboard manipulation',
  'Abusive behaviour toward other players',
  'Fraudulent activity',
  'Terms of service violation',
  'Prize pool abuse',
  'Cheating or collusion',
  'Under 18 — age verification failed',
  'Other',
];

const fns = (name) => httpsCallable(getFunctions(undefined, 'australia-southeast1'), name);

// ── User Search ───────────────────────────────────────────────────────────────

function UserSearch({ onSelect }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [searched,   setSearched]   = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const term      = searchTerm.trim();
      const termLower = term.toLowerCase();

      const [emailSnap, nameSnap, usernameSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'users'),
          where('email',       '>=', termLower),
          where('email',       '<=', termLower + '\uf8ff'),
          limit(10)
        )),
        getDocs(query(
          collection(db, 'users'),
          where('displayName', '>=', term),
          where('displayName', '<=', term + '\uf8ff'),
          limit(10)
        )),
        getDocs(query(
          collection(db, 'users'),
          where('username',    '>=', termLower),
          where('username',    '<=', termLower + '\uf8ff'),
          limit(10)
        )),
      ]);

      const seen = new Set();
      const combined = [];
      [...emailSnap.docs, ...nameSnap.docs, ...usernameSnap.docs].forEach(d => {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          combined.push({ id: d.id, ...d.data() });
        }
      });
      setResults(combined);
    } catch (err) {
      console.error('[AdminUsersPage] Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  return (
    <div className="au-search-section">
      <h2 className="au-section-title">Find a Player</h2>
      <div className="au-search-row">
        <input
          type="text"
          className="au-search-input"
          placeholder="Search by email, display name, or stable name (@bernborough)…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
        />
        <button
          className="au-search-btn"
          onClick={handleSearch}
          disabled={loading || !searchTerm.trim()}
          type="button"
        >
          {loading ? 'Searching…' : '🔍 Search'}
        </button>
      </div>

      {searched && !loading && results.length === 0 && (
        <p className="au-empty">No players found for "{searchTerm}".</p>
      )}

      {results.length > 0 && (
        <div className="au-results">
          {results.map(user => (
            <div key={user.id} className="au-result-row" onClick={() => onSelect(user)}>
              <div className="au-result-info">
                <span className="au-result-name">{user.displayName || '—'}</span>
                {user.username && (
                  <span className="au-result-username">@{user.username}</span>
                )}
                <span className="au-result-email">{user.email}</span>
              </div>
              <div className="au-result-meta">
                <span className={`au-status-pill au-status-pill--${user.status || 'active'}`}>
                  {user.status === 'suspended' ? '🔴 Suspended' : '🟢 Active'}
                </span>
                <span className="au-result-role">{user.role || 'player'}</span>
              </div>
              <span className="au-result-arrow">›</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Suspension Log ────────────────────────────────────────────────────────────

function SuspensionLog({ userId }) {
  const [log,     setLog]     = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    if (!userId) return;
    getDocs(col(db, 'users', userId, 'suspension_log'))
      .then(snap => {
        const entries = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aMs = a.createdAt?.toMillis?.() ?? 0;
            const bMs = b.createdAt?.toMillis?.() ?? 0;
            return bMs - aMs;
          });
        setLog(entries);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <p className="au-empty">Loading history…</p>;
  if (log.length === 0) return <p className="au-empty">No suspension history.</p>;

  return (
    <div className="au-log">
      {log.map(entry => {
        const date = entry.createdAt?.toDate?.()?.toLocaleDateString('en-AU', {
          day: 'numeric', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        }) || '—';
        return (
          <div key={entry.id} className={`au-log-entry au-log-entry--${entry.type}`}>
            <div className="au-log-entry-header">
              <span className="au-log-type">
                {entry.type === 'suspension' ? '🔴 Suspended' : '🟢 Reinstated'}
              </span>
              <span className="au-log-date">{date}</span>
            </div>
            {entry.reason && <p className="au-log-detail"><strong>Reason:</strong> {entry.reason}</p>}
            {entry.action && <p className="au-log-detail"><strong>Action:</strong> {entry.action}</p>}
            {entry.note   && <p className="au-log-detail"><strong>Note:</strong> {entry.note}</p>}
          </div>
        );
      })}
    </div>
  );
}

// ── User Detail Panel ─────────────────────────────────────────────────────────

function UserDetailPanel({ user, onBack, onActionComplete }) {
  const [tab,          setTab]          = useState('suspend');
  const [reason,       setReason]       = useState('');
  const [reasonOther,  setReasonOther]  = useState('');
  const [action,       setAction]       = useState('');
  const [apology,      setApology]      = useState('');
  const [contactEmail, setContactEmail] = useState('admin@syndicatorshowdown.com.au');
  const [reinstateNote, setReinstateNote] = useState('');

  // Deactivate form state
  const [deactivateReason,  setDeactivateReason]  = useState('');
  const [deactivateOther,   setDeactivateOther]   = useState('');
  const [deactivateAction,  setDeactivateAction]  = useState('');
  const [deactivateApology, setDeactivateApology] = useState('');

  // Permanent ban form state
  const [banReason,    setBanReason]    = useState('');
  const [banOther,     setBanOther]     = useState('');
  const [banConfirmed, setBanConfirmed] = useState(false);
  const [banTyped,     setBanTyped]     = useState('');

  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState(null);

  // Reset Password state
  const [resetPasswordNote,    setResetPasswordNote]    = useState('');
  const [resetPasswordStatus,  setResetPasswordStatus]  = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Reset PIN state
  const [resetPinNote,    setResetPinNote]    = useState('');
  const [resetPinStatus,  setResetPinStatus]  = useState(null);
  const [resetPinLoading, setResetPinLoading] = useState(false);

  const isSuspended = user.status === 'suspended';
  const finalReason = reason === 'Other' ? reasonOther : reason;

  const handleSuspend = async () => {
    if (!finalReason || !action) {
      setError('Please fill in the reason and course of action.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await fns('suspendUserAccount')({ targetUid: user.id, reason: finalReason, action, apology, contactEmail });
      setResult(`✓ Account suspended. Email sent to ${user.email}.`);
      onActionComplete?.();
    } catch (err) {
      setError(err.message || 'Failed to suspend account.');
    } finally {
      setLoading(false);
    }
  };

  const handleReinstate = async () => {
    setLoading(true);
    setError(null);
    try {
      await fns('reinstateUserAccount')({ targetUid: user.id, note: reinstateNote, contactEmail });
      setResult(`✓ Account reinstated. Email sent to ${user.email}.`);
      onActionComplete?.();
    } catch (err) {
      setError(err.message || 'Failed to reinstate account.');
    } finally {
      setLoading(false);
    }
  };

  async function handleResetPassword() {
    setResetPasswordLoading(true);
    setResetPasswordStatus(null);
    try {
      await fns('adminResetPassword')({
        targetUid: user.id,
        adminNote: resetPasswordNote.trim() || null,
      });
      setResetPasswordStatus({ type: 'success', msg: `✓ Password reset email sent to ${user.email}` });
      setResetPasswordNote('');
    } catch (e) {
      setResetPasswordStatus({ type: 'error', msg: `Failed: ${e.message}` });
    } finally {
      setResetPasswordLoading(false);
    }
  }

  async function handleResetPin() {
    const confirmed = window.confirm(
      `Generate a temporary PIN for ${user.displayName || user.email} and email it to ${user.email}?`
    );
    if (!confirmed) return;
    setResetPinLoading(true);
    setResetPinStatus(null);
    try {
      await fns('adminResetPin')({
        targetUid: user.id,
        adminNote: resetPinNote.trim() || null,
      });
      setResetPinStatus({ type: 'success', msg: `✓ Temporary PIN generated and emailed to ${user.email}. Valid for 24 hours.` });
      setResetPinNote('');
    } catch (e) {
      setResetPinStatus({ type: 'error', msg: `Failed: ${e.message}` });
    } finally {
      setResetPinLoading(false);
    }
  }

  return (
    <div className="au-detail-panel">
      <button className="au-back-btn" onClick={onBack} type="button">← Back to search</button>

      <div className="au-user-header">
        <div className="au-user-avatar">
          {(user.displayName || user.email || '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="au-user-info">
          <h2 className="au-user-name">{user.displayName || '—'}</h2>
          <p className="au-user-email">{user.email}</p>
          <div className="au-user-meta">
            <span className={`au-status-pill au-status-pill--${user.status || 'active'}`}>
              {user.status === 'suspended' ? '🔴 Suspended' : '🟢 Active'}
            </span>
            <span className="au-user-uid">UID: {user.id}</span>
          </div>
        </div>
      </div>

      {result && <div className="au-result-banner">{result}</div>}

      <div className="au-tabs">
        <button className={`au-tab ${tab === 'suspend'    ? 'au-tab--active' : ''}`} onClick={() => setTab('suspend')}    type="button">🔴 Suspend</button>
        <button className={`au-tab ${tab === 'reinstate'  ? 'au-tab--active' : ''}`} onClick={() => setTab('reinstate')}  type="button">🟢 Reinstate</button>
        <button className={`au-tab ${tab === 'deactivate' ? 'au-tab--active' : ''}`} onClick={() => setTab('deactivate')} type="button">🔕 Deactivate</button>
        <button className={`au-tab ${tab === 'ban'        ? 'au-tab--active' : ''}`} onClick={() => setTab('ban')}        type="button">🚫 Permanent Ban</button>
        <button className={`au-tab ${tab === 'log'           ? 'au-tab--active' : ''}`} onClick={() => setTab('log')}           type="button">📋 History</button>
        <button className={`au-tab ${tab === 'resetPassword' ? 'au-tab--active' : ''}`} onClick={() => setTab('resetPassword')} type="button">🔑 Reset Password</button>
        <button className={`au-tab ${tab === 'resetPin'      ? 'au-tab--active' : ''}`} onClick={() => setTab('resetPin')}      type="button">🔢 Reset PIN</button>
      </div>

      {tab === 'suspend' && !result && (
        <div className="au-form">
          {isSuspended && (
            <div className="au-warning-banner">
              ⚠ This account is already suspended. Submitting will re-send the suspension email.
            </div>
          )}
          <div className="au-field">
            <label className="au-label">REASON FOR SUSPENSION</label>
            <select className="au-select" value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">— Select a reason —</option>
              {SUSPENSION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {reason === 'Other' && (
            <div className="au-field">
              <label className="au-label">SPECIFY REASON</label>
              <input type="text" className="au-input" placeholder="Describe the reason…" value={reasonOther} onChange={e => setReasonOther(e.target.value)} />
            </div>
          )}
          <div className="au-field">
            <label className="au-label">COURSE OF ACTION</label>
            <p className="au-hint">This is shown to the player in the email.</p>
            <textarea className="au-textarea" rows={3} placeholder="e.g. Your account has been suspended for 30 days pending review." value={action} onChange={e => setAction(e.target.value)} />
          </div>
          <div className="au-field">
            <label className="au-label">APOLOGY / ADDITIONAL CONTEXT <span className="au-optional">(optional)</span></label>
            <textarea className="au-textarea" rows={2} placeholder="e.g. We understand this may be frustrating and appreciate your patience." value={apology} onChange={e => setApology(e.target.value)} />
          </div>
          <div className="au-field">
            <label className="au-label">CONTACT EMAIL</label>
            <input type="email" className="au-input" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
          </div>
          {error && <p className="au-error">{error}</p>}
          <button className="au-submit-btn au-submit-btn--suspend" onClick={handleSuspend} disabled={loading} type="button">
            {loading ? '⏳ Suspending…' : '🔴 Suspend Account & Send Email'}
          </button>
        </div>
      )}

      {tab === 'reinstate' && !result && (
        <div className="au-form">
          {!isSuspended && (
            <div className="au-warning-banner">⚠ This account is not currently suspended.</div>
          )}
          <div className="au-field">
            <label className="au-label">REINSTATEMENT NOTE <span className="au-optional">(optional)</span></label>
            <p className="au-hint">Any additional context to include in the reinstatement email.</p>
            <textarea className="au-textarea" rows={3} placeholder="e.g. After review we have determined no breach occurred." value={reinstateNote} onChange={e => setReinstateNote(e.target.value)} />
          </div>
          <div className="au-field">
            <label className="au-label">CONTACT EMAIL</label>
            <input type="email" className="au-input" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
          </div>
          {error && <p className="au-error">{error}</p>}
          <button className="au-submit-btn au-submit-btn--reinstate" onClick={handleReinstate} disabled={loading} type="button">
            {loading ? '⏳ Reinstating…' : '🟢 Reinstate Account & Send Email'}
          </button>
        </div>
      )}

      {/* Deactivate form */}
      {tab === 'deactivate' && !result && (
        <div className="au-form">
          <div className="au-warning-banner">
            🔕 Deactivation disables the account and adds the email to the exclusion
            list. It can be reversed by an admin. For permanent closure use the
            Permanent Ban tab.
          </div>

          <div className="au-field">
            <label className="au-label">REASON FOR DEACTIVATION</label>
            <select
              className="au-select"
              value={deactivateReason}
              onChange={e => setDeactivateReason(e.target.value)}
            >
              <option value="">— Select a reason —</option>
              {DEACTIVATION_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {deactivateReason === 'Other' && (
            <div className="au-field">
              <label className="au-label">SPECIFY REASON</label>
              <input
                type="text"
                className="au-input"
                placeholder="Describe the reason…"
                value={deactivateOther}
                onChange={e => setDeactivateOther(e.target.value)}
              />
            </div>
          )}

          <div className="au-field">
            <label className="au-label">COURSE OF ACTION</label>
            <textarea
              className="au-textarea"
              rows={3}
              placeholder="e.g. Your account has been deactivated. Contact us to discuss reinstatement."
              value={deactivateAction}
              onChange={e => setDeactivateAction(e.target.value)}
            />
          </div>

          <div className="au-field">
            <label className="au-label">
              ADDITIONAL CONTEXT <span className="au-optional">(optional)</span>
            </label>
            <textarea
              className="au-textarea"
              rows={2}
              placeholder="Any additional context or apology to include in the email."
              value={deactivateApology}
              onChange={e => setDeactivateApology(e.target.value)}
            />
          </div>

          {error && <p className="au-error">{error}</p>}

          <button
            className="au-submit-btn"
            style={{ background: '#6B7280', color: '#fff' }}
            onClick={async () => {
              const finalReason = deactivateReason === 'Other'
                ? deactivateOther : deactivateReason;
              if (!finalReason || !deactivateAction) {
                setError('Please fill in the reason and course of action.');
                return;
              }
              setLoading('deactivate');
              setError(null);
              try {
                await fns('deactivateUserAccount')({
                  targetUid:    user.id,
                  reason:       finalReason,
                  action:       deactivateAction,
                  apology:      deactivateApology,
                  contactEmail,
                });
                setResult(`✓ Account deactivated. Email sent to ${user.email}.`);
                onActionComplete?.();
              } catch (err) {
                setError(err.message || 'Failed to deactivate account.');
              } finally {
                setLoading(null);
              }
            }}
            disabled={!!loading}
            type="button"
          >
            {loading === 'deactivate' ? '⏳ Deactivating…' : '🔕 Deactivate Account & Send Email'}
          </button>
        </div>
      )}

      {/* Permanent Ban form */}
      {tab === 'ban' && !result && (
        <div className="au-form">
          <div className="au-error" style={{ background: '#FEF2F2', fontSize: 13 }}>
            ⚠️ <strong>This action is permanent.</strong> The account will be
            disabled and the email address will be permanently blocked from
            re-registering. This cannot be undone through the admin panel.
          </div>

          <div className="au-field">
            <label className="au-label">REASON FOR PERMANENT BAN</label>
            <select
              className="au-select"
              value={banReason}
              onChange={e => setBanReason(e.target.value)}
            >
              <option value="">— Select a reason —</option>
              {PERMANENT_BAN_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {banReason === 'Other' && (
            <div className="au-field">
              <label className="au-label">SPECIFY REASON</label>
              <input
                type="text"
                className="au-input"
                placeholder="Describe the reason…"
                value={banOther}
                onChange={e => setBanOther(e.target.value)}
              />
            </div>
          )}

          <div className="au-field">
            <label className="au-label">CONFIRMATION</label>
            <p className="au-hint">
              Type <strong>PERMANENT BAN</strong> below to confirm this action.
            </p>
            <input
              type="text"
              className="au-input"
              placeholder="Type PERMANENT BAN to confirm"
              value={banTyped}
              onChange={e => setBanTyped(e.target.value)}
            />
          </div>

          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            cursor: 'pointer', fontSize: 13, color: '#374151', lineHeight: 1.5,
          }}>
            <input
              type="checkbox"
              checked={banConfirmed}
              onChange={e => setBanConfirmed(e.target.checked)}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            I confirm this is a permanent, irreversible action and the email address
            {' '}<strong>{user.email}</strong>{' '}
            will be permanently blocked from Syndicator Showdown.
          </label>

          {error && <p className="au-error">{error}</p>}

          <button
            className="au-submit-btn"
            style={{
              background: banTyped === 'PERMANENT BAN' && banConfirmed
                ? '#7F1D1D' : '#D1D5DB',
              color: banTyped === 'PERMANENT BAN' && banConfirmed ? '#fff' : '#9CA3AF',
              cursor: banTyped === 'PERMANENT BAN' && banConfirmed
                ? 'pointer' : 'not-allowed',
            }}
            onClick={async () => {
              const finalReason = banReason === 'Other' ? banOther : banReason;
              if (!finalReason) {
                setError('Please select a reason.');
                return;
              }
              if (banTyped !== 'PERMANENT BAN') {
                setError('Please type PERMANENT BAN to confirm.');
                return;
              }
              if (!banConfirmed) {
                setError('Please check the confirmation box.');
                return;
              }
              setLoading('ban');
              setError(null);
              try {
                await fns('permanentlyBanUser')({
                  targetUid: user.id,
                  reason:    finalReason,
                  confirmed: true,
                });
                setResult(`✓ Account permanently banned. Email sent to ${user.email}.`);
                onActionComplete?.();
              } catch (err) {
                setError(err.message || 'Failed to permanently ban account.');
              } finally {
                setLoading(null);
              }
            }}
            disabled={!!loading || banTyped !== 'PERMANENT BAN' || !banConfirmed}
            type="button"
          >
            {loading === 'ban' ? '⏳ Processing…' : '🚫 Permanently Ban Account'}
          </button>
        </div>
      )}

      {tab === 'log' && (
        <div className="au-form">
          <h3 className="au-section-title">Suspension History</h3>
          <SuspensionLog userId={user.id} />
        </div>
      )}

      {tab === 'resetPassword' && (
        <div className="au-form">
          <h3 className="au-section-title">Reset Password</h3>
          <p className="au-hint" style={{ marginBottom: 16 }}>
            Sends a password reset email to <strong>{user.email}</strong>.
            The link expires in <strong>1 hour</strong>. The user must click the link
            and set a new password before it expires.
          </p>
          <div className="au-field">
            <label className="au-label">NOTE TO USER (optional)</label>
            <textarea
              className="au-textarea"
              rows={3}
              placeholder="e.g. Your password was reset following a support request."
              value={resetPasswordNote}
              onChange={e => setResetPasswordNote(e.target.value)}
            />
            <p className="au-hint">This note is included in the email sent to the player.</p>
          </div>
          {resetPasswordStatus && (
            <p className={resetPasswordStatus.type === 'success' ? 'au-result-banner' : 'au-error'}>
              {resetPasswordStatus.msg}
            </p>
          )}
          <button
            className="au-submit-btn"
            style={{ background: '#126DFF', color: '#fff' }}
            onClick={handleResetPassword}
            disabled={resetPasswordLoading}
            type="button"
          >
            {resetPasswordLoading ? '⏳ Sending…' : `🔑 Send Password Reset to ${user.email}`}
          </button>
        </div>
      )}

      {tab === 'resetPin' && (
        <div className="au-form">
          <h3 className="au-section-title">Reset PIN</h3>
          <p className="au-hint" style={{ marginBottom: 16 }}>
            Generates a temporary 6-digit PIN and emails it to <strong>{user.email}</strong>.
            Valid for <strong>24 hours</strong>. The user will be prompted to set a new PIN
            immediately after logging in with the temporary one.
          </p>
          {!user.pinEnabled && (
            <div className="au-warning-banner">
              ℹ️ This player does not currently have a PIN enabled.
              Resetting will enable PIN login with a temporary PIN.
            </div>
          )}
          <div className="au-field">
            <label className="au-label">NOTE TO USER (optional)</label>
            <textarea
              className="au-textarea"
              rows={3}
              placeholder="e.g. Your PIN was reset following a support request."
              value={resetPinNote}
              onChange={e => setResetPinNote(e.target.value)}
            />
          </div>
          {resetPinStatus && (
            <p className={resetPinStatus.type === 'success' ? 'au-result-banner' : 'au-error'}>
              {resetPinStatus.msg}
            </p>
          )}
          <button
            className="au-submit-btn"
            style={{ background: '#126DFF', color: '#fff' }}
            onClick={handleResetPin}
            disabled={resetPinLoading}
            type="button"
          >
            {resetPinLoading ? '⏳ Generating…' : `🔢 Generate Temp PIN & Email ${user.email}`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AdminUsersPage() {
  const [selectedUser, setSelectedUser] = useState(null);

  return (
    <AdminLayout>
      <div className="au-page">
        <div className="au-page-header">
          <h1 className="au-page-title">👤 User Management</h1>
          <p className="au-page-subtitle">Search for players, view account status, and manage suspensions.</p>
        </div>

        {selectedUser ? (
          <UserDetailPanel
            user={selectedUser}
            onBack={() => setSelectedUser(null)}
            onActionComplete={() => {
              setSelectedUser(prev => ({
                ...prev,
                status: prev.status === 'suspended' ? 'active' : 'suspended',
              }));
            }}
          />
        ) : (
          <UserSearch onSelect={setSelectedUser} />
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminUsersPage;
